import { useEffect, useRef, useState } from "react";
import { Instagram, Mail, Phone } from "lucide-react";
import { AssetImage, ReplaceSlot } from "@/components/site/AssetImage";
import { NewsletterForm } from "@/components/site/NewsletterForm";
import { useSite } from "@/context/SiteContext";
import { PHONE, PHONE_HREF } from "@/lib/assets";

const linkCls = "inline-flex min-h-[44px] items-center gap-3 rounded-lg text-[#1B3530] underline-offset-4 hover:underline";

export function Footer() {
  const { settings } = useSite();
  const ref = useRef(null);
  const [paused, setPaused] = useState(true);
  const email = settings?.public_email || "enza.boccuzzi13@gmail.com";

  useEffect(() => {
    const el = ref.current;
    if (!("IntersectionObserver" in window)) return setPaused(false);
    const io = new IntersectionObserver(([e]) => setPaused(!e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <footer ref={ref} className="of-aurora mt-24 text-[#1B3530]" data-paused={paused} data-testid="site-footer">
      <div className="of-container grid gap-12 py-16 md:grid-cols-[1.2fr_1fr_1.3fr]">
        <div>
          <AssetImage asset="LOGO" size="xs" decorative sizes="168px" replaceClass="!bottom-auto !left-0 top-full mt-1 w-[168px] !max-w-none" className="mb-4 h-16 w-24 rounded-2xl" testId="footer-logo" />
          <p className="font-display text-2xl font-semibold">Olivia's Foundation</p>
          <p className="mt-1 text-sm font-semibold text-[#A8283F]">Fighting Cancer for Kids</p>
          <p className="mt-5 max-w-sm leading-relaxed" data-testid="footer-501c3-text">
            Olivia's Foundation is a registered 501(c)(3). Donations are tax-deductible.
          </p>
          <p className="mt-3 text-sm text-[#2B3833]">All-volunteer. 100% of donations go to the mission.</p>
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold">Say hello</h2>
          <ul className="mt-3 space-y-1">
            <li><a href={PHONE_HREF} className={linkCls} data-testid="footer-phone-link"><Phone className="h-4 w-4" aria-hidden />{PHONE}</a></li>
            <li><a href={`mailto:${email}`} className={`${linkCls} break-all`} data-testid="footer-email-link"><Mail className="h-4 w-4 shrink-0" aria-hidden />{email}</a></li>
            <li>
              <a href={settings?.facebook_url || "https://www.facebook.com/oliviasfoundation"} target="_blank" rel="noopener noreferrer" className={linkCls} data-testid="footer-facebook-link">
                <AssetImage asset="FB_LOGO" size="xs" decorative noEdit sizes="208px" className="h-9 w-[104px] rounded-lg" testId="footer-fb-logo" />
                Facebook<span className="sr-only"> (opens in a new tab)</span>
              </a>
              <span className="relative block h-9 w-[168px]"><ReplaceSlot asset="FB_LOGO" className="!bottom-0 !left-0" /></span>
            </li>
            <li>
              <a href={settings?.instagram_url || "https://www.instagram.com/oliviasfoundation"} target="_blank" rel="noopener noreferrer" className={linkCls} data-testid="footer-instagram-link">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/70"><Instagram className="h-4 w-4" aria-hidden /></span>
                Instagram<span className="sr-only"> (opens in a new tab)</span>
              </a>
            </li>
          </ul>
        </div>
        <div className="rounded-[28px] bg-white/60 p-5 backdrop-blur-sm">
          <h2 className="font-display text-lg font-semibold">Hear from us, gently</h2>
          <p className="mb-4 mt-2 text-[#2B3833]">One or two emails a year. That's it.</p>
          <NewsletterForm idPrefix="footer" />
        </div>
      </div>
      <div className="border-t border-[#24453D]/15">
        <div className="of-container flex flex-col gap-1 py-6 text-sm text-[#2B3833] sm:flex-row sm:justify-between">
          <p>© {new Date().getFullYear()} Olivia's Foundation · Brooklyn, NY</p>
          <p>Awareness equals Funding equals Research equals A Cure</p>
        </div>
      </div>
    </footer>
  );
}
